import { Component, OnInit, inject } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { CreateFeedbackRequest } from '../../../models/feedback.models';
import { LookupOption, LookupTypeCodes } from '../../../models/lookup.models';
import { FeedbackService } from '../../../services/feedback.service';
import { LookupService } from '../../../services/lookup.service';
import { SnackbarService } from '../../../services/snackbar.service';
import { ConfirmationDialogComponent } from '../confirmation-dialog/confirmation-dialog.component';
import { describeApiError } from '../../../utils/api-error.utils';

@Component({
  selector: 'app-feedback-dialog',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatSelectModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: './feedback-dialog.component.html',
  styleUrls: ['./feedback-dialog.component.scss'],
})
export class FeedbackDialogComponent implements OnInit {
  private dialogRef =
    inject<MatDialogRef<FeedbackDialogComponent>>(MatDialogRef);
  private fb = inject(FormBuilder);
  private dialog = inject(MatDialog);
  private feedbackService = inject(FeedbackService);
  private lookupService = inject(LookupService);
  private snackbarService = inject(SnackbarService);

  categories: LookupOption[] = [];

  feedbackForm!: FormGroup;
  isSaving = false;

  // Drives the dialog body: show a spinner while the category lookup loads, an
  // error + retry if it fails (or returns nothing), or the form once categories
  // are available. Prevents the "empty form you can't do anything with" when the
  // FEEDBACK_CATEGORY lookup is unavailable.
  loadState: 'loading' | 'error' | 'ready' = 'loading';

  ngOnInit(): void {
    this.feedbackForm = this.fb.group({
      category: ['', [Validators.required]],
      message: [
        '',
        [Validators.required, Validators.maxLength(4000)],
      ],
    });

    this.loadCategories();
  }

  /** Loads category options from the FEEDBACK_CATEGORY lookup (sorted by sortOrder). */
  loadCategories(): void {
    this.loadState = 'loading';

    this.lookupService
      .getLookupOptions(LookupTypeCodes.FEEDBACK_CATEGORY)
      .subscribe({
        next: (options) => {
          // No active categories means the form would be unusable — treat it the
          // same as a load failure so the user gets a clear message and Retry.
          if (!options.length) {
            console.error('No feedback categories returned from lookup.');
            this.loadState = 'error';
            return;
          }

          this.categories = options;
          // Default to the first option without marking the form dirty (keeps Send disabled
          // until the user actually edits something).
          if (!this.feedbackForm.get('category')?.value) {
            this.feedbackForm.patchValue(
              { category: options[0].value },
              { emitEvent: false },
            );
          }
          this.loadState = 'ready';
        },
        error: (err) => {
          console.error('Error loading feedback categories:', err);
          this.loadState = 'error';
        },
      });
  }

  onSubmit(): void {
    if (this.feedbackForm.invalid || this.isSaving) {
      this.feedbackForm.markAllAsTouched();
      return;
    }

    this.isSaving = true;

    const request: CreateFeedbackRequest = {
      category: this.feedbackForm.value.category,
      message: (this.feedbackForm.value.message ?? '').trim(),
      pageUrl: window.location.pathname,
    };

    this.feedbackService.submitFeedback(request).subscribe({
      next: () => {
        this.snackbarService.showSuccess('Thanks for your feedback!');
        this.dialogRef.close(true);
      },
      error: (error) => {
        console.error('Failed to submit feedback:', error);
        this.snackbarService.showError(
          describeApiError(error, 'Failed to submit feedback. Please try again.'),
        );
        this.isSaving = false;
      },
    });
  }

  onCancel(): void {
    if (this.isSaving) {
      return;
    }

    // No edits yet — close immediately.
    if (!this.feedbackForm.dirty) {
      this.dialogRef.close();
      return;
    }

    // Confirm discarding unsaved changes before closing.
    this.dialog
      .open(ConfirmationDialogComponent, {
        width: '400px',
        disableClose: true,
        data: {
          title: 'Discard feedback?',
          message:
            'You have unsaved changes. If you close now, your feedback will not be sent.',
          confirmText: 'Discard',
          cancelText: 'Keep editing',
          icon: 'warning',
          confirmColor: 'var(--dark-red-error)',
        },
      })
      .afterClosed()
      .subscribe((confirmed) => {
        if (confirmed) {
          this.dialogRef.close();
        }
      });
  }
}
